<template>
  <main class="calendar-page">
    <div v-if="!isAuth" class="calendar-page__notice" role="status">
      Нет данных авторизации от родительского приложения.
      Откройте календарь из основной страницы с параметром con_str.
    </div>
    <template v-else>
      <nav class="calendar-page__nav" aria-label="Режим модуля">
        <button type="button" :aria-pressed="!diagnostics" @click="setMode(false)">
          Календарь
        </button>
        <button type="button" :aria-pressed="diagnostics" @click="setMode(true)">
          Проверка связи с БД
        </button>
      </nav>
      <section v-if="diagnostics" class="calendar-probe">
        <h1>Проверка iframe → API → БД</h1>
        <p>Пациент с основной страницы: <strong>{{ idPatient || 'не выбран' }}</strong></p>
        <p>Отправка добавляет ID в тестовую таблицу. Чтение показывает строки из этой таблицы.</p>
        <div class="calendar-probe__actions">
          <button type="button" :disabled="busy || !idPatient" @click="probe('send')">
            Отправить ID — send_id
          </button>
          <button type="button" :disabled="busy" @click="probe('receive')">
            Прочитать ID — receive_id
          </button>
        </div>
        <p role="status" aria-live="polite">{{ probeMessage }}</p>
        <table v-if="rows.length">
          <thead><tr><th>ID строки</th><th>ID пациента</th></tr></thead>
          <tbody><tr v-for="(row, index) in rows" :key="index">
            <td>{{ row.id }}</td><td>{{ row.id_patient }}</td>
          </tr></tbody>
        </table>
      </section>
      <CalendarWidget
        v-else
        :key="contextGeneration"
        :context="calendarContext"
        :request="calendarStore.requestCalendar"
      />
    </template>
  </main>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import CalendarWidget from '~components/CalendarWidget.vue';
// The existing auth store imports the router; it is read after setup starts.
// eslint-disable-next-line import/no-cycle
import { useAuthStore } from '@/store/auth/authModule';
import { useCalendarStore } from '@/store/calendar/calendarModule';
import { parseIdPatient } from '~utils/parentAuth';

const route = useRoute();
const router = useRouter();
const calendarStore = useCalendarStore();
const { isAuth } = storeToRefs(useAuthStore());
const { idPatient } = storeToRefs(calendarStore);
const contextGeneration = ref(0);
const busy = ref(false);
const probeMessage = ref('');
const rows = ref<Array<{ id: unknown; id_patient: unknown }>>([]);
const diagnostics = computed(() => route.query.mode === 'diagnostics');
const calendarContext = computed(() => ({
  id_patient: idPatient.value || null,
  id_staff: parseIdPatient(route.query.id_staff) || null,
  id_branch: parseIdPatient(route.query.id_branch) || null,
  ...(typeof route.query.date === 'string' ? { date: route.query.date } : {}),
}));

// Remount even if only authorization changed; old responses cannot paint a new session.
watch(() => [route.query.id_patient, route.query.id_staff, route.query.id_branch,
  route.query.date, route.query.con_str], () => {
  contextGeneration.value += 1;
  calendarStore.setPatient(parseIdPatient(route.query.id_patient));
  rows.value = [];
  probeMessage.value = '';
  busy.value = false;
}, { immediate: true });

function setMode(showDiagnostics: boolean) {
  router.replace({ query: { ...route.query, mode: showDiagnostics ? 'diagnostics' : undefined } });
}

async function probe(operation: 'send' | 'receive') {
  if (busy.value) return;
  const generation = contextGeneration.value;
  busy.value = true;
  probeMessage.value = 'Выполняется запрос…';
  try {
    const response = operation === 'send' ? await calendarStore.sendId() : await calendarStore.receiveId();
    if (generation !== contextGeneration.value) return;
    const first = response?.data?.[0];
    if (!response || response.errorType || first?.err) {
      probeMessage.value = String(first?.err_txt || first?.err_text || response?.errorText || 'Нет ответа сервера.');
      return;
    }
    if (operation === 'receive') {
      if (!Array.isArray(response.data)) {
        probeMessage.value = 'Сервер не вернул массив строк.';
        return;
      }
      rows.value = response.data.filter((row) => 'id_patient' in row)
        .map((row) => ({ id: row.id, id_patient: row.id_patient }));
      probeMessage.value = `Получено строк: ${rows.value.length}.`;
    } else probeMessage.value = 'Сервер подтвердил отправку ID. Нажмите «Прочитать ID», чтобы проверить запись.';
  } catch {
    if (generation === contextGeneration.value) probeMessage.value = 'Не удалось выполнить запрос.';
  } finally {
    if (generation === contextGeneration.value) busy.value = false;
  }
}
</script>

<style scoped>
.calendar-page {
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  background: #141b21;
  color: #eef3f8;
}
.calendar-page__notice, .calendar-probe { padding: 24px; }
.calendar-page__nav {
  display: flex;
  gap: 8px;
  padding: 10px 20px;
  border-bottom: 1px solid #33424f;
}
.calendar-page button {
  background: #23313f;
  color: #eef3f8;
  border: 1px solid #526375;
  border-radius: 7px;
  padding: 7px 12px;
  cursor: pointer;
}
.calendar-page button[aria-pressed="true"] { background: #b9d2f0; color: #112235; }
.calendar-page button:disabled { opacity: .5; cursor: default; }
.calendar-probe h1 { font-size: 26px; line-height: 1.3; margin-top: 12px; }
.calendar-probe__actions { display: flex; flex-wrap: wrap; gap: 12px; }
.calendar-probe table { border-collapse: collapse; }
.calendar-probe th, .calendar-probe td {
  border-bottom: 1px solid #526375;
  text-align: left;
  padding: 6px 20px;
}
</style>
